/**
 * URL helpers shared by the client and the server.
 *
 * `isUrl` used to live in `src/client/utils/url.ts`. It moved here so that the
 * asset link form and the server route that persists the asset validate a
 * locator with exactly the same rule instead of drifting apart.
 */

/** Matches an IPv4 literal hostname, e.g. `10.0.0.1`. */
const IPV4_HOSTNAME = /^(\d{1,3}\.){3}\d{1,3}$/;

/** Matches a dotted hostname ending in a plausible TLD, e.g. `example.com`. */
const HOSTNAME_WITH_TLD = /\.[a-z]{2,}$/i;

/**
 * Validates that a string is a well formed `https://` URL.
 *
 * Deliberately strict about the protocol: assets are rendered inside an iframe
 * on a screen, and a plain-http page is blocked as mixed content anyway.
 *
 * Uses the platform URL parser rather than one big regex. The previous regex
 * rejected plenty of legitimate addresses — commas in a query string, slashes
 * or dots in a fragment (i.e. any single-page-app dashboard route), slashes in
 * a query value, underscores in a hostname — which matters because those are
 * exactly the URLs someone wants to put on a screen.
 */
export const isUrl = (value: string): boolean => {
  // The parser silently repairs `https:/host` into `https://host`, so require
  // the literal scheme before handing it over.
  if (!value || !/^https:\/\//i.test(value)) return false;

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;

  const { hostname } = parsed;
  if (!hostname) return false;

  // Require a real TLD (or an IP), so `https://www.google.` and bare
  // single-label hosts such as `localhost` are still rejected — an asset
  // pointing at one of those could never resolve from a screen.
  return IPV4_HOSTNAME.test(hostname) || HOSTNAME_WITH_TLD.test(hostname);
};
